import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Space, 
  DatePicker, 
  Input, 
  Select, 
  Button, 
  Tag, 
  Typography,
  App,
  message,
  Tooltip
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { downloadLogsApi } from '../../services/downloadLogsService';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const DownloadLogs = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    dateRange: null,
    user_name: '',
    task_name: '',
    status: undefined
  });

  const { message } = App.useApp();

  // 获取下载日志
  const fetchDownloadLogs = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        size: pageSize,
        user_name: filters.user_name || undefined,
        task_name: filters.task_name || undefined,
        status: filters.status,
        start_date: filters.dateRange?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
        end_date: filters.dateRange?.[1]?.format('YYYY-MM-DD HH:mm:ss')
      };

      console.log('请求参数:', params);
      const response = await downloadLogsApi.getDownloadLogs(params);
      console.log('下载日志响应:', response);
      
      // 处理标准响应格式
      if (response && response.code === 0 && response.data) {
        setData(response.data.items || []);
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: response.data.total || 0,
        });
      } else {
        console.error('响应格式不正确:', response);
        setData([]);
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: 0,
        });
      }
    } catch (error) {
      message.error('获取下载日志失败');
      console.error('获取下载日志失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理表格变化
  const handleTableChange = (pagination) => {
    fetchDownloadLogs(pagination.current, pagination.pageSize);
  };

  // 处理搜索
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDownloadLogs(1, pagination.pageSize);
  };

  // 处理重置
  const handleReset = () => {
    setFilters({
      dateRange: null,
      user_name: '',
      task_name: '',
      status: undefined
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDownloadLogs(1, pagination.pageSize);
  };

  // 初始化加载
  useEffect(() => {
    fetchDownloadLogs();
  }, []);

  // 表格列定义
  const columns = [
    {
      title: '任务ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 100,
    },
    {
      title: '任务名称',
      dataIndex: 'task_name',
      key: 'task_name',
      width: 300,
    },
    {
      title: '用户名',
      dataIndex: 'user_name',
      key: 'user_name',
      render: (text) => text || '-',
      width: 100,
    },
    {
      title: '下载时间',
      dataIndex: 'download_time',
      key: 'download_time',
      render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-',
      width: 200,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (text) => text || '-',
      width: 100,
    },
    {
      title: '浏览器信息',
      dataIndex: 'user_agent',
      key: 'user_agent',
      render: (text) => text || '-',
      width: 400,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          0: '失败',
          1: '成功'
        };
        return statusMap[status] || '未知';
      },
    },
    {
      title: '错误信息',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 100,
      render: (text) => text || '-',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => fetchDownloadLogs(pagination.current, pagination.pageSize)}
              >
                刷新
              </Button>
            </Space>
            
            <Space wrap>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                allowClear
                showTime
                style={{ width: 300 }}
              />
              <Input
                placeholder="搜索用户名"
                value={filters.user_name}
                onChange={(e) => setFilters(prev => ({ ...prev, user_name: e.target.value }))}
                allowClear
                style={{ width: 150 }}
              />
              <Input
                placeholder="搜索任务名称"
                value={filters.task_name}
                onChange={(e) => setFilters(prev => ({ ...prev, task_name: e.target.value }))}
                allowClear
                style={{ width: 150 }}
              />
              <Select
                placeholder="选择状态"
                value={filters.status}
                onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                allowClear
                style={{ width: 120 }}
              >
                <Select.Option value={1}>成功</Select.Option>
                <Select.Option value={0}>失败</Select.Option>
              </Select>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={pagination}
            loading={loading}
            onChange={handleTableChange}
            locale={{ emptyText: '暂无下载日志数据' }}
          />
        </Space>
      </Card>
    </div>
  );
};

export default DownloadLogs; 